/* Same Skies - shared hardened close net for overlays / drawers / modals.
   window-capture geometric hit-test on the close BUTTON rect. Encodes every guard
   from the LOCKED docs (Overlay Close-Tap Theft 2026-06-17, Multi-Touch 2026-08-29):
   (a) explicit button selector, refuses if it does not resolve to exactly one button
   (b) yields to a real a[href] / button / [role=button] target other than the X
   (c) isPrimary guard on every pointer listener
   (d) touchend scans every changedTouch
   (e) higher-overlay guard: never hijacks a tap that landed inside a visible layer above
   (f) closeFn must be idempotent; the surface keeps its own direct handler too
   Plus: pointer path needs down AND up in rect, padded rect, area cap so a rect that ever
   resolves to a backdrop can never become a click-eater, and the click/touchend that
   trail a pointerup we already consumed are swallowed so nothing under the closed
   overlay gets a ghost click. */
(function () {
  'use strict';
  if (window.ssRegisterDismissible) return;

  var INTERACTIVE = 'a[href], button, [role="button"]';
  var GHOST_MS = 500;
  var GHOST_PX = 24;
  var MAX_AREA = 0.2;

  function isVisible(el) {
    var s = window.getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden' || s.pointerEvents === 'none') return false;
    if (parseFloat(s.opacity || '1') === 0) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  function isButton(el) {
    return !!el && (el.tagName === 'BUTTON' || el.getAttribute('role') === 'button');
  }

  window.ssRegisterDismissible = function (root, closeBtnSelector, closeFn, opts) {
    opts = opts || {};
    var pad = typeof opts.pad === 'number' ? opts.pad : 6;
    var higher = opts.higherOverlays || '';
    var isOpen = typeof opts.isOpen === 'function' ? opts.isOpen : null;
    var rootIsFn = typeof root === 'function';

    if (!rootIsFn && !(root && root.nodeType === 1)) return false;
    if (!rootIsFn) {
      var found = root.querySelectorAll(closeBtnSelector);
      if (found.length !== 1 || !isButton(found[0])) {
        if (window.console) console.warn('[ss-dismissible] refused: selector must match exactly one button', closeBtnSelector, found.length);
        return false;
      }
    }

    var downOnClose = false;
    var consumed = null;

    function resolveRoot() {
      var r = rootIsFn ? root() : root;
      if (!r) return null;
      if (isOpen && !isOpen(r)) return null;
      return r;
    }

    function resolveBtn(r) {
      var list = r.querySelectorAll(closeBtnSelector);
      return list.length === 1 && isButton(list[0]) ? list[0] : null;
    }

    function ownedByHigher(e, r) {
      if (!higher) return false;
      var t = e.target;
      if (!t || t.nodeType !== 1 || !t.closest) return false;
      var h = t.closest(higher);
      if (!h || h === r || r.contains(h)) return false;
      return isVisible(h);
    }

    function yieldsToRealTarget(e, btn) {
      var t = e.target;
      if (!t || t.nodeType !== 1 || !t.closest) return false;
      var hit = t.closest(INTERACTIVE);
      return !!(hit && hit !== btn && !btn.contains(hit));
    }

    function hitAt(x, y, btn) {
      var r = btn.getBoundingClientRect();
      if (!(r.width > 0 && r.height > 0)) return false;
      if (r.width * r.height > MAX_AREA * window.innerWidth * window.innerHeight) return false;
      return x >= r.left - pad && x <= r.right + pad && y >= r.top - pad && y <= r.bottom + pad;
    }

    function closeTapRoot(e, x, y) {
      var r = resolveRoot();
      if (!r) return null;
      if (ownedByHigher(e, r)) return null;
      var btn = resolveBtn(r);
      if (!btn) return null;
      if (yieldsToRealTarget(e, btn)) return null;
      return hitAt(x, y, btn) ? r : null;
    }

    function swallow(e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    }

    function force(e, r, x, y) {
      swallow(e);
      consumed = { t: Date.now(), x: x, y: y };
      closeFn(r);
    }

    function isGhost(x, y) {
      if (!consumed) return false;
      if (Date.now() - consumed.t > GHOST_MS) { consumed = null; return false; }
      return Math.abs(x - consumed.x) <= GHOST_PX && Math.abs(y - consumed.y) <= GHOST_PX;
    }

    window.addEventListener('pointerdown', function (e) {
      if (e.isPrimary === false || e.button > 0) return;
      downOnClose = !!closeTapRoot(e, e.clientX, e.clientY);
    }, true);

    window.addEventListener('pointercancel', function (e) {
      if (e.isPrimary === false) return;
      downOnClose = false;
    }, true);

    window.addEventListener('pointerup', function (e) {
      if (e.isPrimary === false || e.button > 0) return;
      if (!downOnClose) return;
      downOnClose = false;
      var r = closeTapRoot(e, e.clientX, e.clientY);
      if (r) force(e, r, e.clientX, e.clientY);
    }, true);

    window.addEventListener('click', function (e) {
      if (isGhost(e.clientX, e.clientY)) { swallow(e); return; }
      var r = closeTapRoot(e, e.clientX, e.clientY);
      if (r) force(e, r, e.clientX, e.clientY);
    }, true);

    window.addEventListener('touchend', function (e) {
      var ts = e.changedTouches || [];
      var i;
      for (i = 0; i < ts.length; i++) {
        if (isGhost(ts[i].clientX, ts[i].clientY)) { swallow(e); return; }
      }
      for (i = 0; i < ts.length; i++) {
        var r = closeTapRoot(e, ts[i].clientX, ts[i].clientY);
        if (r) { force(e, r, ts[i].clientX, ts[i].clientY); return; }
      }
    }, { capture: true, passive: false });

    return true;
  };
})();
